/**
 * Cache manager - orchestrates caching logic
 */

import type {
  ICacheOptions,
  TCacheStrategy,
  TStandardCacheMode,
} from '../webrequest.types.js';
import { CacheStore } from './cache.store.js';
import {
  getStrategyHandler,
  type IStrategyContext,
  type IStrategyResult,
} from './cache.strategies.js';
import { defaultCacheMaxEntries } from './cache.store.js';
import { createTimeoutError } from '../utils/timeout.js';

export class CacheManager {
  private cacheStore: CacheStore;
  private lifecycleController = new AbortController();
  private readonly closeReason = new Error('Webrequest cache manager is closed');
  private disposed = false;
  private closePromise?: Promise<void>;
  private backgroundRevalidations = new Map<string, Promise<void>>();

  constructor(
    dbName?: string,
    storeName?: string,
    cacheStoreArg?: CacheStore,
  ) {
    this.cacheStore = cacheStoreArg ?? new CacheStore(dbName, storeName);
  }

  /**
   * Execute a request with caching
   */
  public async execute(
    request: Request,
    options: ICacheOptions & { logging?: boolean; timeout?: number },
    fetchFn: (request: Request) => Promise<Response>,
  ): Promise<IStrategyResult> {
    if (this.disposed) {
      throw this.closeReason;
    }
    request.signal.throwIfAborted();

    // Determine the cache strategy
    const strategy = this.determineStrategy(request, options);

    // If no caching (no-store or network-only), bypass cache
    if (strategy === 'network-only') {
      const response = await fetchFn(request);
      return {
        response,
        fromCache: false,
        revalidated: false,
      };
    }

    const cacheMaxEntries =
      options.cacheMaxEntries ?? defaultCacheMaxEntries;
    if (
      !Number.isSafeInteger(cacheMaxEntries) ||
      cacheMaxEntries <= 0
    ) {
      throw new RangeError(
        'webrequest cacheMaxEntries must be a positive safe integer',
      );
    }
    if (
      options.cacheMaxAge !== undefined &&
      (!Number.isSafeInteger(options.cacheMaxAge) ||
        options.cacheMaxAge < 0)
    ) {
      throw new RangeError(
        'webrequest cacheMaxAge must be a non-negative safe integer',
      );
    }

    const timeoutMs = options.timeout ?? 60_000;
    const foregroundDeadlineController = new AbortController();
    const foregroundDeadlineHandle = setTimeout(() => {
      foregroundDeadlineController.abort(
        createTimeoutError(request.url, timeoutMs),
      );
    }, Math.max(0, timeoutMs));
    const operationSignal = AbortSignal.any([
      request.signal,
      this.lifecycleController.signal,
      foregroundDeadlineController.signal,
    ]);

    try {
      // Generate cache key
      const cacheKey = this.generateCacheKey(request, options);

      // Get strategy handler
      const handler = getStrategyHandler(strategy);

      // Execute strategy
      const context: IStrategyContext = {
        request,
        cacheKey,
        cacheStore: this.cacheStore,
        fetchFn,
        logging: options.logging,
        signal: operationSignal,
        cacheMaxAge: options.cacheMaxAge,
        cacheMaxEntries,
        scheduleBackgroundRevalidation: (operationArg) => {
          this.scheduleBackgroundRevalidation(
            cacheKey,
            request.url,
            timeoutMs,
            operationArg,
          );
        },
      };

      return await handler.execute(context);
    } finally {
      clearTimeout(foregroundDeadlineHandle);
    }
  }

  /**
   * Determine the caching strategy based on options and request
   */
  private determineStrategy(
    request: Request,
    options: ICacheOptions,
  ): TCacheStrategy {
    // If explicit strategy provided, use it
    if (options.cacheStrategy) {
      return options.cacheStrategy;
    }

    // Map standard cache modes to strategies
    if (options.cache) {
      return this.mapCacheModeToStrategy(options.cache);
    }

    // Check request cache mode
    if (request.cache) {
      return this.mapCacheModeToStrategy(request.cache as TStandardCacheMode);
    }

    // Default strategy
    return 'network-first';
  }

  /**
   * Map standard fetch cache modes to our strategies
   */
  private mapCacheModeToStrategy(
    cacheMode: TStandardCacheMode,
  ): TCacheStrategy {
    switch (cacheMode) {
      case 'default':
        return 'network-first';
      case 'no-store':
      case 'reload':
        return 'network-only';
      case 'no-cache':
        return 'network-first'; // Will use revalidation
      case 'force-cache':
        return 'cache-first';
      case 'only-if-cached':
        return 'cache-only';
      default:
        return 'network-first';
    }
  }

  /**
   * Generate cache key
   */
  private generateCacheKey(request: Request, options: ICacheOptions): string {
    // If custom cache key provided
    if (options.cacheKey) {
      if (typeof options.cacheKey === 'function') {
        return options.cacheKey(request);
      }
      return options.cacheKey;
    }

    // Default cache key generation
    return this.cacheStore.generateCacheKey(request);
  }

  /**
   * Clear the cache
   */
  public async clear(): Promise<void> {
    await this.cacheStore.clear();
  }

  /**
   * Delete a specific cache entry
   */
  public async delete(cacheKey: string): Promise<void> {
    await this.cacheStore.delete(cacheKey);
  }

  /**
   * Check if a cache entry exists
   */
  public async has(cacheKey: string): Promise<boolean> {
    return await this.cacheStore.has(cacheKey);
  }

  /**
   * Get the underlying cache store
   */
  public getStore(): CacheStore {
    return this.cacheStore;
  }

  private scheduleBackgroundRevalidation(
    cacheKeyArg: string,
    urlArg: string,
    timeoutMsArg: number,
    operationArg: (signalArg: AbortSignal) => Promise<void>,
  ): void {
    if (
      this.disposed ||
      this.backgroundRevalidations.has(cacheKeyArg)
    ) {
      return;
    }
    const deadlineController = new AbortController();
    const signal = AbortSignal.any([
      this.lifecycleController.signal,
      deadlineController.signal,
    ]);
    const timeoutHandle = setTimeout(() => {
      deadlineController.abort(
        createTimeoutError(urlArg, timeoutMsArg),
      );
    }, Math.max(0, timeoutMsArg));
    let trackedOperation!: Promise<void>;
    trackedOperation = operationArg(signal)
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(timeoutHandle);
        if (
          this.backgroundRevalidations.get(cacheKeyArg) ===
          trackedOperation
        ) {
          this.backgroundRevalidations.delete(cacheKeyArg);
        }
      });
    this.backgroundRevalidations.set(cacheKeyArg, trackedOperation);
  }

  /**
   * Stop owned background refreshes and close the persistent cache.
   */
  public close(): Promise<void> {
    if (this.closePromise) {
      return this.closePromise;
    }
    this.disposed = true;
    this.lifecycleController.abort(this.closeReason);
    const backgroundOperations = [
      ...this.backgroundRevalidations.values(),
    ];
    this.closePromise = (async () => {
      await Promise.allSettled(backgroundOperations);
      await this.cacheStore.close();
    })();
    return this.closePromise;
  }
}
